import { ConnectionStatus } from './connection-status';

import type { XiCafeBooking, XiCafeTariff } from './types';

/**
 * Determines the {@link ConnectionStatus} for the XiCafe internet card.
 *
 * Tariffs are person-based (MPI), sessions are device-based (IP).
 * Only active sessions appear in the response — paused sessions are removed.
 *
 * State mapping:
 * 1. API call failed → `Connected` (graceful fallback — see ticket 003)
 * 2. No bookings → `Connected` (on ship WiFi, no tariff purchased)
 * 3. Active booking exhausted → `Exhausted`
 * 4. Active booking, at least one session → `Active`
 * 5. Active booking, no session → `Paused`
 * 6. No ACTIVE booking but at least one exhausted booking → `Exhausted`
 * 7. Otherwise → `Connected`
 *
 * The browser cannot read its own LAN IP without unreliable WebRTC tricks,
 * so the dashboard uses the xapp's documented fallback: any session at all
 * on the booking counts as Active (slightly less precise than per-device).
 */
export const determineXiCafeConnectionStatus = (
  result: { success: true; bookings: XiCafeBooking[] } | { success: false },
): ConnectionStatus => {
  if (!result.success) return ConnectionStatus.Connected;

  const { bookings } = result;
  if (bookings.length === 0) return ConnectionStatus.Connected;

  const booking = selectOptimalBooking(bookings);

  if (booking?.status === 'ACTIVE') {
    if (isBookingExhausted(booking)) return ConnectionStatus.Exhausted;

    const hasAnySession = (booking.sessions?.length ?? 0) > 0;
    return hasAnySession ? ConnectionStatus.Active : ConnectionStatus.Paused;
  }

  if (bookings.some(isBookingExhausted)) return ConnectionStatus.Exhausted;

  return ConnectionStatus.Connected;
};

/**
 * Maps XiCafe bookings to a flat volume summary (used / remaining / max in MB).
 * Returns `null` when there is no displayable booking.
 */
export const mapXiCafeBookingToTariff = (
  bookings: XiCafeBooking[],
): XiCafeTariff | null => {
  if (bookings.length === 0) return null;

  const booking = selectOptimalBooking(bookings);
  if (!booking) return null;

  const traffic = booking.details?.traffic;
  const maxVolume = traffic?.maxValue ?? 0;
  const usedVolume = traffic?.value ?? 0;

  return {
    maxVolume,
    name: booking.name,
    remainingVolume: Math.max(0, maxVolume - usedVolume),
    usedVolume,
  };
};

const isBookingExhausted = (booking: XiCafeBooking): boolean => {
  const { time, traffic } = booking.details ?? {};
  return (
    (!!traffic && traffic.maxValue > 0 && traffic.value >= traffic.maxValue) ||
    (!!time && time.maxValue > 0 && time.value >= time.maxValue)
  );
};

const selectOptimalBooking = (
  bookings: XiCafeBooking[],
): XiCafeBooking | null => {
  const activeWithVolume = bookings.find(
    b => b.status === 'ACTIVE' && !isBookingExhausted(b),
  );
  if (activeWithVolume) return activeWithVolume;

  const active = bookings.find(b => b.status === 'ACTIVE');
  if (active) return active;

  return bookings[0] ?? null;
};
